import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { addLinkMethodSimple } from "@/modules/billing/choose_method/addMethodUtils.ts";
import { PaymentLinkIcon } from "@/modules/billing/paymentMethodLogic.tsx";
import {
    getPaymentMethodId,
    type CardMethod,
    type LinkMethod,
    type MethodBase,
    type MethodFees,
} from "@/modules/billing/paymentMethods.ts";
import { useQueryClient } from "@tanstack/react-query";
import type {
    LinkConfigBase,
    MethodConfig,
    ProcessorConfig,
    ProcessorCurrencyFee,
    ProcessorServiceFee,
} from "@warrenio/api-spec/spec.oats.gen";
import { useImperativeHandle } from "react";

/** @returns Default properties for a payment method (based on standard {@link config} fields) */
export function defaults(config: ProcessorConfig, method?: MethodConfig) {
    const id = getPaymentMethodId(config, method);
    return {
        id,
        canAdd: !config.disable_add,
        isAutomated: false,

        name: method ? method.name : config.name,
        icon: (props) => <PaymentLinkIcon icon={id} {...props} />,
    } satisfies Partial<MethodBase>;
}

export function cardDefaults(config: ProcessorConfig & { type: "creditcard" }) {
    const base = defaults(config);
    return {
        ...base,

        type: "creditcard",
        isAutomated: config?.supports_auto_topup ?? true,

        fee: getFee(config),
    } as const satisfies Partial<CardMethod>;
}

/** @returns Default properties for a `link`-type payment method */
export function linkDefaults(config: ProcessorConfig & LinkConfigBase, method: MethodConfig) {
    const base = defaults(config, method);
    const paymentMethodId = base.id;

    return {
        ...base,
        fee: getFee(config),
        type: "link",
        AddForm({ actionsRef }) {
            const queryClient = useQueryClient();
            const apiClient = useApiClient();

            useImperativeHandle(actionsRef, () => ({
                // eslint-disable-next-line @typescript-eslint/require-await
                async validate() {
                    return true;
                },
                async addPaymentMethod(params) {
                    await addLinkMethodSimple(queryClient, apiClient, params, paymentMethodId);
                },
            }));

            return null;
        },
    } satisfies Partial<LinkMethod>;
}

function getFee(config: {
    service_fee?: ProcessorServiceFee;
    charge_currency_conversion?: ProcessorCurrencyFee;
}): MethodFees {
    return config.service_fee != null || config.charge_currency_conversion != null
        ? {
              service_fee: config.service_fee,
              charge_currency_conversion: config.charge_currency_conversion,
          }
        : undefined;
}
